import { MoreHorizontal, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { TimePeriod } from "@/components/charts";

interface Reading {
  id: string;
  date: string;
  time: string;
  value: number;
  unit: string;
  label?: string;
  sampleType?: string;
  status?: "normal" | "warning" | "critical";
}

interface TrackerDataListProps {
  readings: Reading[];
  selectedPeriod: TimePeriod;
  currentDate: Date;
  metricColor: string;
  metricUnit: string;
  onDelete: (index: number, readingId: string) => void;
  formatDateDisplay: (period: TimePeriod) => string;
}

const statusConfig = {
  normal: {
    icon: CheckCircle2,
    label: "Normal",
    color: "text-success",
    bgColor: "bg-success/10",
    borderColor: "border-success/20",
  },
  warning: {
    icon: AlertCircle,
    label: "Elevated",
    color: "text-warning",
    bgColor: "bg-warning/10",
    borderColor: "border-warning/20",
  },
  critical: {
    icon: AlertCircle,
    label: "Critical",
    color: "text-destructive",
    bgColor: "bg-destructive/10",
    borderColor: "border-destructive/20",
  },
};

export const TrackerDataList = ({
  readings,
  selectedPeriod,
  metricColor,
  metricUnit,
  onDelete,
}: TrackerDataListProps) => {
  if (readings.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center">
          <span className="text-muted-foreground text-lg">📊</span>
        </div>
        <p className="text-foreground text-sm font-medium">
          No readings available
        </p>
        <p className="text-muted-foreground text-xs mt-1">
          Add a new reading to see your data
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Readings
        </span>
        <span className="text-xs text-muted-foreground">
          {readings.length} {readings.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      {/* List */}
      <div className="max-h-[280px] overflow-y-auto">
        {readings.map((reading, index) => {
          const status = reading.status ? statusConfig[reading.status] : null;
          const StatusIcon = status?.icon;

          return (
            <div
              key={reading.id}
              className={cn(
                "group flex items-center justify-between px-4 py-3",
                "border-b border-border last:border-b-0",
                "hover:bg-muted/50 transition-colors duration-150"
              )}
            >
              <div className="flex items-center gap-3">
                {/* Color indicator */}
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-2 ring-background"
                  style={{ backgroundColor: metricColor }}
                />

                {/* Time and Value */}
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    {selectedPeriod === "day" ? reading.time : reading.date}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-base font-semibold text-foreground">
                      {reading.value}{" "}
                      <span className="text-sm font-normal text-muted-foreground">
                        {metricUnit}
                      </span>
                    </span>
                    {status && StatusIcon && (
                      <span
                        className={cn(
                          "flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border",
                          status.bgColor,
                          status.color,
                          status.borderColor
                        )}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    )}
                  </div>
                  {reading.sampleType && (
                    <span className="text-xs text-muted-foreground mt-0.5">
                      {reading.sampleType}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32">
                  <DropdownMenuItem
                    onClick={() => onDelete(index, reading.id)}
                    className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>
    </div>
  );
};
